import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  Zap,
  FileText,
  Smartphone,
  Send,
  PoundSterling,
  ArrowRight,
  Wrench,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PAGE_URL = "https://thepropertool.com/tradesman-quoting-tool";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Tradesman Quoting Tool",
  "description": "Tradesman quoting tool. Generate professional, itemised quotes from your phone or desk in under 10 minutes. Built for electricians, plumbers, decorators, plasterers, and roofers.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Tradesman Quoting Tool", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best tradesman quoting tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For sole-trader tradespeople and small trade businesses in the UK, The Proper Tool is purpose-built: a UK cost library across 31 trade categories, AI quote generation from a description or site photos, and branded PDF output. Plans start at £29/month with a 14-day free trial and no credit card required.",
      },
    },
    {
      "@type": "Question",
      "name": "Is The Proper Tool good estimating software for tradesmen?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. As estimating software for tradesmen, The Proper Tool generates quantity-based estimates: each line item carries a quantity, unit, and unit rate drawn from a 31-category UK cost library. You describe the job or attach a photo, the AI builds the scope, and you review and send a branded PDF, all in under 10 minutes.",
      },
    },
    {
      "@type": "Question",
      "name": "What trades does The Proper Tool cover?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool covers 31 UK construction trade categories including electrical first and second fix, plumbing and drainage, plastering, decorating, carpentry and joinery, roofing, groundworks, steelwork, landscaping, and more. Every common residential trade is included in a single quote.",
      },
    },
    {
      "@type": "Question",
      "name": "Can I use a tradesman quoting tool on my phone?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool is fully web-based and works on any device including mobile phones and tablets. You can describe the job on-site, attach a photo from your phone, generate the quote, and send the PDF to the client before you leave. No app download required.",
      },
    },
    {
      "@type": "Question",
      "name": "How much does a tradesman quoting tool cost?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool starts at £29/month for sole traders (10 AI quotes per month, unlimited template and manual quotes, professional PDF export, full 31-category cost library). There is also a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and no credit card required.",
      },
    },
    {
      "@type": "Question",
      "name": "What is estimating software for tradesmen?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Estimating software for tradesmen is a tool that helps sole traders and small trade businesses price jobs accurately and quickly. Rather than building a quote in a spreadsheet from scratch, you describe the job (or upload a photo or drawing) and the software generates a fully itemised estimate with UK market rates. The Proper Tool is one such tool, covering 31 trade categories and producing a branded PDF quote in under 10 minutes.",
      },
    },
  ],
};

const FEATURES = [
  {
    icon: Zap,
    title: "Quote any job in under 10 minutes",
    description: "Describe the job in plain language or upload a site photo. The AI generates a fully itemised scope with UK market rates. Done before the kettle's boiled.",
  },
  {
    icon: Smartphone,
    title: "Works on your phone on-site",
    description: "No laptop needed. Open it in your phone browser, describe the job, attach a photo, and send the PDF before you've left the driveway.",
  },
  {
    icon: PoundSterling,
    title: "UK rates built in for your trade",
    description: "462 rated tasks across 31 trade categories with current UK market rates. Electrical, plumbing, plastering, decorating, carpentry, roofing: all covered.",
  },
  {
    icon: FileText,
    title: "Professional PDF output",
    description: "Every quote exports as a branded PDF with your company details, full scope, and pricing. Looks like you spent an hour on it, even if it took 10 minutes.",
  },
  {
    icon: Wrench,
    title: "Override rates with your own",
    description: "Set your own rate on any task in the cost library and it always takes priority on future quotes. Your pricing, your business.",
  },
  {
    icon: Send,
    title: "Send directly from the tool",
    description: "Share a quote PDF by email directly from the app. Clients get a professional document instantly, with no printing or chasing.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Describe the job or take a photo",
    description: "Type what needs doing in plain language, take a photo on-site, or upload any drawings you have. Works from a few lines of text if that's all you've got.",
  },
  {
    step: "2",
    title: "AI generates your itemised quote",
    description: "The AI reads your inputs and produces a fully itemised scope of works: every trade item, quantities, and unit rates from the UK cost library. Takes under 10 minutes.",
  },
  {
    step: "3",
    title: "Review, adjust, and send the PDF",
    description: "Check every line, add or remove items, adjust any price you want, then export a branded PDF and send it to the client.",
  },
];

const FAQS = [
  {
    q: "What is the best tradesman quoting tool?",
    a: "For sole-trader tradespeople and small trade businesses in the UK, The Proper Tool is purpose-built: a UK cost library across 31 trade categories, AI quote generation from a description or site photos, and branded PDF output. Plans start at £29/month with a 14-day free trial.",
  },
  {
    q: "Is The Proper Tool good estimating software for tradesmen?",
    a: "Yes. As estimating software for tradesmen, The Proper Tool generates quantity-based estimates: each line item carries a quantity, unit, and unit rate drawn from a 31-category UK cost library. You describe the job or attach a photo, the AI builds the scope, and you review and send a branded PDF, all in under 10 minutes.",
  },
  {
    q: "What trades does The Proper Tool cover?",
    a: "The Proper Tool covers 31 UK construction trade categories including electrical first and second fix, plumbing and drainage, plastering, decorating, carpentry and joinery, roofing, groundworks, steelwork, landscaping, and more. Every common residential trade is included in a single quote.",
  },
  {
    q: "Can I use a tradesman quoting tool on my phone?",
    a: "Yes. The Proper Tool is fully web-based and works on any device including mobile phones and tablets. You can describe the job on-site, attach a photo from your phone, generate the quote, and send the PDF to the client before you leave. No app download required.",
  },
  {
    q: "How much does a tradesman quoting tool cost?",
    a: "The Proper Tool starts at £29/month for sole traders (10 AI quotes per month, unlimited template and manual quotes, professional PDF export, full 31-category cost library). There is also a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and no credit card required.",
  },
  {
    q: "What is estimating software for tradesmen?",
    a: "Estimating software for tradesmen is a tool that helps sole traders and small trade businesses price jobs accurately and quickly. Rather than building a quote from scratch in a spreadsheet, you describe the job (or upload a photo) and the software generates a fully itemised estimate with UK market rates. The Proper Tool covers 31 trade categories and produces a branded PDF quote in under 10 minutes.",
  },
];

export default function TradesmanQuotingToolPage() {

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Tradesman Quoting Tool | The Proper Tool"
        description="Tradesman quoting tool and estimating software for tradesmen. Generate professional, itemised quotes on your phone in under 10 minutes. For electricians, plumbers, plasterers, decorators, and roofers. From £29/mo."
        canonical="/tradesman-quoting-tool"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Tradesman Quoting Tool</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Tradesman Quoting Tool
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-4 max-w-3xl mx-auto">
              Generate professional, itemised quotes on your phone in under 10 minutes. Built for sole-trader tradespeople: electricians, plumbers, plasterers, decorators, and roofers.
            </p>
            <p className="text-base text-muted-foreground mb-8 max-w-2xl mx-auto">
              Also the best <strong className="text-foreground">estimating software for tradesmen</strong>, offering quantity-based cost build-ups with UK market rates across 31 trade categories.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/quoting-software-for-builders">See all features</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · No credit card required</p>
          </div>
        </section>

        {/* Who it's for */}
        <section className="py-14 border-t border-border/40 bg-muted/20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">Built for sole-trader tradespeople</h2>
              <p className="text-muted-foreground">Covers every common residential trade, all in one place.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {([
                { label: "Electricians", href: "/quoting-software-for-electricians" },
                { label: "Plumbers", href: "/quoting-software-for-plumbers" },
                { label: "Plasterers", href: "/quoting-software-for-plasterers" },
                { label: "Decorators", href: "/quoting-software-for-decorators" },
                { label: "Roofers", href: "/quoting-software-for-roofers" },
                { label: "Carpenters", href: "/quoting-software-for-carpenters" },
                { label: "Joiners", href: null },
                { label: "Groundworkers", href: null },
                { label: "General Builders", href: null },
                { label: "Tilers", href: null },
                { label: "Drainage Contractors", href: null },
                { label: "Landscapers", href: null },
              ] as { label: string; href: string | null }[]).map(({ label, href }) =>
                href ? (
                  <Link key={label} href={href} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-background text-sm font-medium hover:border-primary/60 hover:bg-primary/5 transition-colors">
                    <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                    {label}
                  </Link>
                ) : (
                  <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-background text-sm font-medium">
                    <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                    {label}
                  </span>
                )
              )}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="how-it-works">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Quote on site in three steps</h2>
              <p className="text-lg text-muted-foreground">From arriving at the job to having a PDF in the client's inbox.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-8">
              {HOW_IT_WORKS.map((step) => (
                <div key={step.step} className="relative">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature grid */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="features">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Everything a tradesperson needs to quote quickly</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Stop spending evenings writing quotes in Excel. The Proper Tool does it in the time it takes to drive home from the job.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f) => (
                <div key={f.title} className="bg-background rounded-xl border border-border p-6">
                  <f.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Estimating software for tradesmen section */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Estimating software for tradesmen: how it works</h2>
            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
              As estimating software for tradesmen, The Proper Tool goes beyond a simple price calculator. Every quote is a <strong className="text-foreground">quantity-based estimate</strong>: each line item carries a quantity (m², m, nr), a unit rate from the UK cost library, and a full cost breakdown. This means your quote reflects the actual scope of the job, not just a round number that's hard to justify.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { title: "Quantity-based line items", desc: "Every item has a quantity, unit, and unit rate. The AI derives quantities from your description or from uploaded drawings." },
                { title: "UK market rates for your trade", desc: "Electricians, plumbers, plasterers, and roofers each have their own rate entries. Override any rate with your own and it always takes priority." },
                { title: "Labour / materials / plant split", desc: "Each line item carries a labour and materials cost split so you can see at a glance where your margin is coming from." },
                { title: "Export a full estimate as a PDF", desc: "The client-facing PDF shows a full itemised scope of works, not just a total. Clients can see exactly what they're paying for." },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">{item.title}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-primary mb-2">10 min</p>
                <p className="text-muted-foreground">Average time to generate a full itemised quote</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">462</p>
                <p className="text-muted-foreground">UK construction rates across 31 trade categories</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">£29/mo</p>
                <p className="text-muted-foreground">Starting price for sole traders</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple pricing for tradespeople</h2>
              <p className="text-lg text-muted-foreground">Start with a 14-day free trial. No credit card required.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {([
                { name: "Lite", price: "£29", desc: "For sole traders", features: ["10 AI auto-quotes/mo", "Unlimited template & manual quotes", "Professional PDF export", "Full 31-category cost library"], highlight: false },
                { name: "Starter", price: "£59", desc: "For growing businesses", features: ["25 AI auto-quotes/mo", "Everything in Lite", "Pricing Intelligence", "Expert validation pipeline", "Regional pricing"], highlight: true },
                { name: "Pro", price: "£99", desc: "For high-volume tradespeople", features: ["50 AI auto-quotes/mo", "Everything in Starter", "Drawing analysis", "Learned rate adjustments"], highlight: false },
              ] as { name: string; price: string; desc: string; features: string[]; highlight: boolean }[]).map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${plan.highlight ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">{plan.price}<span className="text-base font-normal text-muted-foreground">/mo</span></p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.highlight ? "default" : "outline"} asChild className="w-full">
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related guides</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/quoting-software-for-electricians", label: "Quoting Software for Electricians" },
                { href: "/quoting-software-for-plumbers", label: "Quoting Software for Plumbers" },
                { href: "/quoting-software-for-roofers", label: "Quoting Software for Roofers" },
                { href: "/quoting-software-for-plasterers", label: "Quoting Software for Plasterers" },
                { href: "/quoting-software-for-decorators", label: "Quoting Software for Decorators" },
                { href: "/quoting-software-for-carpenters", label: "Quoting Software for Carpenters" },
                { href: "/quoting-software-for-builders", label: "Quoting Software for Builders" },
                { href: "/quoting-software-for-contractors", label: "Quoting Software for Contractors" },
                { href: "/job-quoting-software", label: "Job Quoting Software" },
                { href: "/quoting-app-for-builders", label: "Quoting App for Builders" },
                { href: "/blog/builder-quote-app-uk", label: "The Best Builder Quote App for UK Tradespeople" },
                { href: "/blog/free-quoting-software-builders-uk", label: "Free Quoting Software for Builders UK" },
                { href: "/free-trial/", label: "Free Trial" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Start quoting faster today</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join tradespeople using The Proper Tool to send professional quotes in minutes and win more jobs.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free 14-day trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
